import { Fragment, useEffect, useState } from "react";
import { DEFAULT_SITE_TEXT, SITE_TEXT_MAX, PALETTES, ageBandsOf, type SiteText } from "@greg-chick/shared";
import { getSiteText, saveSiteText } from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { Msg } from "./ui";

/**
 * 홈 화면 문구 편집(FR-LAND).
 *
 * "체험판 공개 → 정식 오픈", "여름 축제 중" 같은 문구는 행사 주기마다 바뀐다.
 * 코드에 박혀 있으면 한 줄 고치는 데 배포가 필요하다 — 여기서 바로 바꾼다.
 * 저장하면 지금 홈을 보고 있는 사람 화면에도 바로 반영된다(SSE).
 */
export const FIELDS: { key: keyof SiteText; label: string; help: string; group?: string }[] = [
  {
    key: "milestone",
    label: "작은 배지",
    help: "제목 위 작은 딱지. 비우면 아예 안 보여요. 🧸 영유아 모드 첫 화면에서는 안 보여요(보호자 화면에서만)",
    group: "🏠 홈 화면",
  },
  { key: "title1", label: "큰 제목 첫 줄", help: "예: 그레그와 병아리의" },
  { key: "title2", label: "큰 제목 둘째 줄", help: "굵게 나와요. 예: 경주 그림책방" },
  /**
   * 👶 조치계획서 ■9 「첫 화면과 책 소개에 연령 명시」(10. 10.) — 약속은 「관리자 문구 화면에서」인데 칸이 없었다.
   * 기본이 비어 있다: 운영자가 넣고 전후 화면을 남기는 것이 증빙이다. 도움말에 조치계획서 문구를 그대로 둔다.
   */
  {
    key: "ageBands",
    label: "몇 살 것인지(연령 딱지)",
    help:
      "| 로 나눠 적으면 첫 화면 아래(친구 만들기 단추 밑)와 [전자책 고르기] 제목 밑에 작은 딱지로 떠요(4개까지). 영유아 모드에서도 보여요. 비우면 사라져요. " +
      "짧게 써야 폰에서 두 줄 안에 들어가요. 조치계획서 세 단: 0~3세 종이 초점책|2세~ 전자책 함께 보기|4~7세 마을·카메라 마법 " +
      "(「보호자와 함께」 같은 자세한 말은 [보호자 안내]의 「몇 살에 무엇을」 칸에)",
  },
  { key: "bubble", label: "캐릭터 말풍선", help: "왼쪽 친구 머리 위에 뜨는 말" },
  { key: "cta", label: "큰 입장 버튼", help: "가장 큰 버튼에 적히는 말" },
  /**
   * 🔴 이 칸이 **목록에 없었다**(2026-09-21 `siteTextFields.test.ts` 가 찾음). 문구 설명에는
   * 「비우면 그 단추만 사라진다 — 첫 화면을 단순하게 두고 싶으면 운영자가 지우면 된다」고
   * 적혀 있는데, 관리자에 칸이 없어서 **지울 방법이 없었다.** 주석이 약속한 것을 코드가 안 했다.
   */
  {
    key: "makeHeroCta",
    label: "친구 만들기 버튼",
    help: "비우면 이 버튼이 사라져요(첫 화면을 단순하게 두고 싶을 때). 이미 친구가 있는 아이에겐 안 떠요",
  },
  { key: "note", label: "아래 안내 문구", help: "히어로를 아직 안 만든 분에게 보여요. 🧸 영유아 모드 첫 화면에서는 안 보여요" },
  { key: "newsEmpty", label: "소식이 없을 때", help: "소식이 없을 때 뜨는 말 — 첫 화면의 🔔 과 [커뮤니티] 화면 **두 곳**에 같이 나와요" },
  {
    key: "resumeCta",
    label: "이어서 읽기 버튼",
    help: "읽다 만 책이 있을 때만 떠요. 뒤에 책 이름이 자동으로 붙어요",
  },

  { key: "bookKicker", label: "작은 윗말", help: "예: 그림책방. 🧸 영유아 모드에서는 안 보여요", group: "📖 전자책 고르기" },
  { key: "bookTitle", label: "큰 제목", help: "예: 어떤 이야기를 펼쳐 볼까요?" },
  { key: "bookSub", label: "아래 안내", help: "뒤에 '· 모두 N권'이 자동으로 붙어요. 🧸 영유아 모드에서는 안 보여요" },

  {
    key: "endTitle",
    label: "축하 한 줄",
    help: "마지막 쪽을 넘기면 크게 떠요",
    group: "🎉 책을 다 읽었을 때",
  },
  { key: "endSub", label: "아래 한 줄", help: "비우면 이 줄만 사라져요" },
  {
    key: "endAgain",
    label: "다시 읽기 버튼",
    help: "누르면 1쪽으로 돌아가요. 아이들이 가장 많이 누르는 버튼이에요",
  },
  { key: "endMore", label: "다른 책 버튼", help: "책 고르기 화면으로 가요" },
  {
    key: "endWorld",
    label: "체험 공간 버튼",
    help: "비우면 이 버튼이 사라져요. 행사 때만 켜 두셔도 돼요",
  },
  {
    key: "endTogether",
    label: "보호자 줄 제목",
    help: "아이가 눌러 본 마법의 「보호자에게」 문구를 접어서 모아 보여 줘요. 비우면 이 줄이 사라져요",
  },

  {
    key: "storyWho",
    label: "누가 (낱말 조각)",
    help: "| 로 나눠 적어요. 예: 내가|그레그가|병아리가 — 아이가 눌러서 문장을 만들어요(10개까지)",
    group: "📖 내 그림책 — 문장 조각",
  },
  { key: "storyWhere", label: "어디서 (낱말 조각)", help: "예: 첨성대에서|도서관에서" },
  { key: "storyWhat", label: "무엇을 (낱말 조각)", help: "문장을 끝내는 말이에요. 예: 별을 셌어요|폴짝 뛰었어요" },
  {
    key: "storyTitles",
    label: "책 제목 예시",
    help: "{이름} 자리에 아이 별명이 들어가요. 예: {이름}의 모험|별을 찾아서",
  },
  {
    key: "storyStickers",
    label: "붙일 수 있는 스티커",
    help: "그림 이름을 | 로 나눠 적어요. npc.<친구> · decor.<소품>만 돼요. 예: npc.chick|decor.lantern",
  },

  {
    key: "artStyle",
    label: "그림체 설명",
    help: "[➕ 직접 만들기]의 AI가 이 결로 그려요. 비우면 기본(연필+수채)으로 그려요",
    group: "🎨 AI 그림 결",
  },

  {
    key: "verseTitle",
    label: "화면 제목",
    help: "예: 체험 공간 들어가기",
    group: "⭐ 체험 공간 들어가기",
  },
  { key: "verseEnter", label: "들어가는 버튼", help: "가장 큰 버튼에 적히는 말" },
  {
    key: "verseNoteSaved",
    label: "히어로가 있을 때",
    help: "만들어 둔 히어로가 있는 아이에게 보여요",
  },
  { key: "verseNoteNew", label: "히어로가 없을 때", help: "아직 안 만든 아이에게 보여요" },

  { key: "guideTitle", label: "화면 제목", help: "예: 보호자님께", group: "🧑‍🍼 보호자 안내" },
  { key: "guideIntro", label: "여는 글", help: "이 서비스가 무엇인지 두세 줄로. 비우면 안 나와요" },
  // 🔴 화면에 나오는 **차례대로** 둔다(Guide.tsx) — 번호와 자리가 어긋나면 운영자가
  //    「② 를 고쳤는데 세 번째 칸이 바뀐다」를 겪는다(전에 ③·④ 가 그랬다).
  { key: "guideAgeTitle", label: "① 칸 제목", help: "예: 👶 몇 살에 무엇을 하나요" },
  {
    key: "guideAgeBody",
    label: "① 내용",
    help: "보호자가 이 화면에서 제일 먼저 찾는 것이에요(몇 살 것인지·얼마나 보는지)",
  },
  { key: "guideSafetyTitle", label: "② 칸 제목", help: "예: 🧸 아이가 안전하게" },
  { key: "guideSafetyBody", label: "② 내용", help: "비우면 이 칸이 통째로 사라져요" },
  { key: "guideCameraTitle", label: "③ 칸 제목", help: "예: 📷 카메라를 쓸 때" },
  { key: "guideCameraBody", label: "③ 내용", help: "밝은 곳·보호자 동반·사진은 기기 안에서만" },
  { key: "guidePrivacyTitle", label: "④ 칸 제목", help: "예: 🔒 개인정보를 받지 않아요" },
  { key: "guidePrivacyBody", label: "④ 내용", help: "비우면 이 칸이 통째로 사라져요" },
  { key: "guideClassTitle", label: "⑤ 칸 제목", help: "예: 🏫 방과후 수업에 참여한다면" },
  {
    key: "guideClassBody",
    label: "⑤ 내용",
    help: "보존 기간 문장은 [🏫 방과후 수업]에서 고른 값이 자동으로 뒤에 붙어요",
  },
  { key: "guideTogetherTitle", label: "⑥ 칸 제목", help: "예: 👪 이렇게 같이 놀아요" },
  { key: "guideTogetherBody", label: "⑥ 내용", help: "비우면 이 칸이 통째로 사라져요" },
  {
    key: "familyBookTitle",
    label: "👪 우리 아이 그림책 — 제목",
    help: "부모님이 QR 로 여는 화면의 제목",
  },
  { key: "familyBookNoteTitle", label: "👪 아래 칸 제목", help: "예: 👪 집에서도 이어서" },
  {
    key: "familyBookNoteBody",
    label: "👪 아래 칸 내용",
    help: "비우면 칸이 통째로 사라져요. 다음 학기·굿즈 안내를 적으세요",
  },
  { key: "familyBookLinkLabel", label: "👪 버튼 글자", help: "예: 다음 학기 알아보기" },
  {
    key: "familyBookLinkUrl",
    label: "👪 버튼 주소",
    help: "비어 있거나 REPLACE 면 버튼이 안 떠요(준비 중인 링크를 누르게 하지 않아요)",
  },

  { key: "askTitle", label: "제목", help: "마을 소식 맨 아래 문의 칸", group: "🙋 문의 안내" },
  { key: "askBody", label: "설명", help: "댓글·글쓰기를 왜 안 두는지 보호자에게 설명해요" },
  { key: "askCta", label: "버튼", help: "외부 폼으로 가는 버튼에 적히는 말" },
];

export function SiteTextPanel() {
  const [text, setText] = useState<SiteText>(DEFAULT_SITE_TEXT);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getSiteText()
      .then((t) => {
        setText(t);
        setLoaded(true);
      })
      .catch(() => setMsg("엣지 서버에 연결하지 못했어요."));
  }, []);

  async function onSave() {
    const t = await saveSiteText(text);
    setText(t);
    setMsg("✅ 저장했어요. 홈 화면에 바로 보여요.");
  }
  useRegisterSave({
    id: "sitetext",
    label: "홈 문구",
    save: onSave,
    draft: { data: () => text, restore: (d) => setText(d as SiteText) },
  });

  function resetAll() {
    setText(DEFAULT_SITE_TEXT);
    setMsg("처음 문구로 되돌렸어요. [저장]을 눌러야 실제로 반영돼요.");
  }

  return (
    <section className="panel sitetext">
      <h2 className="panel__h">글</h2>
      <p className="hint">
        첫 화면에 적히는 말들이에요. 행사가 바뀔 때 <b>여기만 고치면</b> 돼요.
      </p>

      <div className="sitetext__preview" aria-hidden="true">
        {text.milestone ? <span className="sitetext__badge">{text.milestone}</span> : null}
        <strong className="sitetext__title">
          {text.title1}
          <br />
          {text.title2}
        </strong>
        <span className="sitetext__bubble">{text.bubble}</span>
        <span className="sitetext__cta">{text.cta}</span>
        <small>{text.note}</small>
        {/* 👶 연령 딱지 — 아이 화면과 같은 판정(ageBandsOf)으로 나눈다 */}
        {ageBandsOf(text.ageBands).length ? (
          <span className="sitetext__ages">
            {ageBandsOf(text.ageBands).map((b) => (
              <em key={b}>{b}</em>
            ))}
          </span>
        ) : null}
      </div>

      {/* 색은 **고르는 것**이지 만드는 것이 아니다 — 색상환을 주면 대비가 깨진다.
          여기 있는 팔레트는 전부 대비 검사(palette.test.ts)를 통과한 것이다. */}
      <div className="sitetext__theme">
        <h4 className="sitetext__group">🎨 색</h4>
        <div className="sitetext__swatches">
          {PALETTES.map((p) => (
            <button
              key={p.id}
              type="button"
              className="sitetext__swatch"
              data-on={text.theme === p.id || undefined}
              onClick={() => setText((t) => ({ ...t, theme: p.id }))}
              title={p.hint}
            >
              <span
                style={{ background: `linear-gradient(180deg, ${p.brandLit}, ${p.brandDeep})` }}
              />
              <span style={{ background: `linear-gradient(180deg, ${p.ctaFrom}, ${p.ctaTo})` }} />
              <b>{p.label}</b>
              <small>{p.hint}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="sitetext__fields">
        {FIELDS.filter((f) => f.key !== "theme").map((f) => (
          <Fragment key={f.key}>
            {f.group ? <h4 className="sitetext__group">{f.group}</h4> : null}
            <label>
              <span className="sitetext__label">
                {f.label}
                <em>
                  {text[f.key].length}/{SITE_TEXT_MAX[f.key]}
                </em>
              </span>
              <input
                value={text[f.key]}
                maxLength={SITE_TEXT_MAX[f.key]}
                placeholder={DEFAULT_SITE_TEXT[f.key]}
                onChange={(e) => setText((t) => ({ ...t, [f.key]: e.target.value }))}
              />
              <small>{f.help}</small>
            </label>
          </Fragment>
        ))}
      </div>

      <Msg text={msg} />

      <div className="sitetext__actions">
        <button className="btn btn--sm" onClick={resetAll} disabled={!loaded}>
          처음 문구로 되돌리기
        </button>
      </div>
    </section>
  );
}
